/**
 * A new random UUID v4. Works in browsers, Workers, and Node (global Web Crypto).
 * crypto.randomUUID only exists in secure contexts (HTTPS or localhost), so on a phone
 * opening the dev server over plain http it falls back to building one from getRandomValues.
 */
export function newId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40; // version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Deterministic id for a per-user singleton row (one row per user per key), e.g.
 * singletonId(userId, 'module_states.spending') or singletonId(userId, 'budget_settings').
 * Two offline devices that create the same singleton produce the same id, so sync merges
 * them into one row (last write wins per field) instead of creating duplicates.
 */
export function singletonId(userId: string, key: string): string {
  if (userId === '' || key === '') throw new Error('singletonId needs a userId and a key');
  if (userId.includes(':')) throw new Error(`userId must not contain ':', got '${userId}'`);
  return `${userId}:${key}`;
}

export type SeedKind = 'category' | 'tile';

/**
 * Deterministic id for a row the app seeds (conventions §3), built on singletonId:
 *   seededId(u, 'category', 'food')                  -> 'u:categories.food'
 *   seededId(u, 'tile', 'spending', 'spending.today') -> 'u:tiles.spending.spending.today'
 * The tile form equals v0.1c's defaultTileId(userId, dashboardId, type).
 * Seeds are inserted with ifAbsent, so an existing row (renamed, hidden, deleted) is never reset.
 */
export function seededId(userId: string, kind: SeedKind, ...parts: string[]): string {
  if (parts.length === 0 || parts.some((p) => p === ''))
    throw new Error('seededId needs non-empty parts');
  const table = kind === 'category' ? 'categories' : 'tiles';
  return singletonId(userId, `${table}.${parts.join('.')}`);
}
